import type { ReactNode } from "react";
import {
  Avatar,
  Button,
  Text,
  makeStyles,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import { Link } from "@tanstack/react-router";
import { useOkta } from "../auth/OktaProvider";

const useStyles = makeStyles({
  shell: {
    minHeight: "100vh",
    display: "grid",
    gridTemplateRows: "auto 1fr",
    backgroundColor: tokens.colorNeutralBackground2,
  },
  topbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    ...shorthands.padding("12px", "24px"),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderBottom("1px", "solid", tokens.colorNeutralStroke2),
  },
  brand: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorBrandForeground1,
  },
  user: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
  },
  body: {
    display: "grid",
    gridTemplateColumns: "240px 1fr",
    minHeight: 0,
  },
  nav: {
    ...shorthands.padding("16px"),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRight("1px", "solid", tokens.colorNeutralStroke2),
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
  },
  navLink: {
    color: tokens.colorNeutralForeground1,
    textDecorationLine: "none",
    ...shorthands.padding("8px", "12px"),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  main: {
    ...shorthands.padding("24px"),
    overflowY: "auto",
  },
});

export function RootLayout({ children }: { children: ReactNode }) {
  const styles = useStyles();
  const { isAuthenticated, user, signOut } = useOkta();

  const handleLogout = () => {
    void signOut();
  };

  const displayName = user?.name ?? user?.preferred_username ?? user?.email ?? "";

  return (
    <div className={styles.shell}>
      <header className={styles.topbar}>
        <Text size={500} className={styles.brand}>
          BriefTaker
        </Text>
        <div className={styles.user}>
          {isAuthenticated && displayName ? (
            <>
              <Avatar name={displayName} size={32} />
              <Text>{displayName}</Text>
              <Button appearance="secondary" onClick={handleLogout}>
                Se deconnecter
              </Button>
            </>
          ) : (
            <Text italic>Non connecte</Text>
          )}
        </div>
      </header>
      <div className={styles.body}>
        <nav className={styles.nav} aria-label="Navigation principale">
          <Link to="/briefs/new" className={styles.navLink}>
            Nouveau brief
          </Link>
        </nav>
        <main className={styles.main}>{children}</main>
      </div>
    </div>
  );
}
